import React from "react";
import PropTypes from "prop-types";
import { ErrorMessage, FieldArray, useFormikContext } from "formik";
import classNames from "classnames";

import { states } from "../states";
import TextField from "./text-field";
import SelectField from "./select-field";

// @todo Translate
export const defaultCompositeAddressElements = [
  {
    id: "first_name",
    "#type": "textfield",
    "#title": "First name",
    "#description": null,
    "#placeholder": null,
    "#required": true,
    "#size": null,
    "#minlength": null,
    "#maxlength": null,
    "#multiple": null,
    "#states": null,
  },
  {
    id: "last_name",
    "#type": "textfield",
    "#title": "Last name",
    "#description": null,
    "#placeholder": null,
    "#required": true,
    "#size": null,
    "#minlength": null,
    "#maxlength": null,
    "#multiple": null,
    "#states": null,
  },
  {
    id: "organization",
    "#type": "textfield",
    "#title": "Organisation",
    "#description": null,
    "#placeholder": null,
    "#required": null,
    "#size": null,
    "#minlength": null,
    "#maxlength": null,
    "#multiple": null,
    "#states": null,
  },
  {
    id: "address_line_1",
    "#type": "textfield",
    "#title": "Street address",
    "#description": null,
    "#placeholder": null,
    "#required": null,
    "#size": null,
    "#minlength": null,
    "#maxlength": null,
    "#multiple": null,
    "#states": null,
  },
  {
    id: "address_line_2",
    "#type": "textfield",
    "#title": "",
    "#description": null,
    "#placeholder": null,
    "#required": null,
    "#size": null,
    "#minlength": null,
    "#maxlength": null,
    "#multiple": null,
    "#states": null,
  },
  {
    id: "postal_code",
    "#type": "textfield",
    "#title": "Postleitzahl",
    "#description": null,
    "#placeholder": null,
    "#required": null,
    "#size": null,
    "#minlength": null,
    "#maxlength": null,
    "#multiple": null,
    "#states": null,
  },
  {
    id: "locality",
    "#type": "textfield",
    "#title": "Ort",
    "#description": null,
    "#placeholder": null,
    "#required": null,
    "#size": null,
    "#minlength": null,
    "#maxlength": null,
    "#multiple": null,
    "#states": null,
  },
  {
    id: "country",
    "#type": "select",
    "#description": null,
    "#title": "Land",
    "#options": {
      DE: "Deutschland",
      BE: "Belgien",
      FR: "Frankreich",
    },
    "#required": true,
    "#multiple": null,
    "#states": null,
  },
];

export const getItemCompositeAddressElements = (item) => {
  const optionsToCheck = ["title", "description", "placeholder", "required"];

  return defaultCompositeAddressElements.map((compositeElement) => {
    for (const option of optionsToCheck) {
      if (
        Object.prototype.isPrototypeOf.call(
          item,
          `#${compositeElement.id}__${option}`
        )
      ) {
        return {
          ...compositeElement,
          "#title": item[`#${compositeElement.id}__${option}`],
        };
      }
    }

    return compositeElement;
  });
};

const CompositeAddressField = ({
  item,
  generatedInitialValues,
  compositeIndex,
  compositeParent,
}) => {
  const { values } = useFormikContext();

  const { invisible, visible, optional, required } = states(
    item["#states"],
    values
  );

  const initialValue = compositeParent
    ? values[compositeParent][compositeIndex][item.id]
    : values[item.id];

  const showCompositeField = (field) => {
    return (
      (Object.prototype.isPrototypeOf.call(item, `#${field}__access`) &&
        item[`${field}__access`]) ||
      !Object.prototype.isPrototypeOf.call(item, `#${field}__access`)
    );
  };

  const processedElements = getItemCompositeAddressElements(item);

  return (
    <div
      className={classNames({
        "form-group webform-composite-address": true,
        hidden: invisible || !visible,
      })}
      style={item["#flex"] ? { flex: item["#flex"] } : {}}
    >
      <label htmlFor={item.id}>
        <h3>
          {item["#title"]}
          {(!!item["#required"] || required) && !optional && visible && (
            <span className="required">*</span>
          )}
        </h3>
      </label>

      <FieldArray
        name={item.id}
        render={(arrayHelpers) => (
          <>
            {[...Array(initialValue.length)].map((i, index) => (
              <React.Fragment key={index}>
                {showCompositeField("first_name") ||
                showCompositeField("last_name") ? (
                  <div className="form-row name">
                    {showCompositeField("first_name") && (
                      <div className="col-16 col-md-8">
                        <TextField
                          item={processedElements[0]}
                          compositeParent={
                            compositeParent
                              ? `${compositeParent}.${compositeIndex}.${item.id}`
                              : item.id
                          }
                          compositeIndex={undefined}
                        />
                      </div>
                    )}
                    {showCompositeField("last_name") && (
                      <div className="col-16 col-md-8">
                        <TextField
                          item={processedElements[1]}
                          compositeParent={
                            compositeParent
                              ? `${compositeParent}.${compositeIndex}.${item.id}`
                              : item.id
                          }
                          compositeIndex={undefined}
                        />
                      </div>
                    )}
                  </div>
                ) : null}
                <div className="form-row">
                  {showCompositeField("organization") && (
                    <div className="col-16">
                      <TextField
                        item={processedElements[2]}
                        compositeParent={
                          compositeParent
                            ? `${compositeParent}.${compositeIndex}.${item.id}`
                            : item.id
                        }
                        compositeIndex={undefined}
                      />
                    </div>
                  )}
                  {showCompositeField("address_line_1") ||
                  showCompositeField("address_line_2") ? (
                    <div className="col-16 street-address">
                      {showCompositeField("address_line_1") && (
                        <TextField
                          item={processedElements[3]}
                          compositeParent={
                            compositeParent
                              ? `${compositeParent}.${compositeIndex}.${item.id}`
                              : item.id
                          }
                          compositeIndex={undefined}
                        />
                      )}
                      {showCompositeField("address_line_2") && (
                        <TextField
                          item={processedElements[4]}
                          compositeParent={
                            compositeParent
                              ? `${compositeParent}.${compositeIndex}.${item.id}`
                              : item.id
                          }
                          compositeIndex={undefined}
                        />
                      )}
                    </div>
                  ) : null}
                </div>
                {showCompositeField("postal_code") ||
                showCompositeField("locality") ? (
                  <div className="form-row">
                    {showCompositeField("postal_code") && (
                      <div className="col-16 col-md-4">
                        <TextField
                          item={processedElements[5]}
                          compositeParent={
                            compositeParent
                              ? `${compositeParent}.${compositeIndex}.${item.id}`
                              : item.id
                          }
                          compositeIndex={undefined}
                        />
                      </div>
                    )}
                    {showCompositeField("locality") && (
                      <div className="col-16 col-md-12">
                        <TextField
                          item={processedElements[6]}
                          compositeParent={
                            compositeParent
                              ? `${compositeParent}.${compositeIndex}.${item.id}`
                              : item.id
                          }
                          compositeIndex={undefined}
                        />
                      </div>
                    )}
                  </div>
                ) : null}
                {showCompositeField("country") && (
                  <div className="form-row">
                    <div className="col-16">
                      <SelectField
                        item={processedElements[7]}
                        compositeParent={
                          compositeParent
                            ? `${compositeParent}.${compositeIndex}.${item.id}`
                            : item.id
                        }
                        compositeIndex={undefined}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}

            {!!item["#multiple"] &&
            (item["#multiple"] === 0 ||
              item["#multiple"] > initialValue.length) ? (
              <div>
                <button
                  type="button"
                  onClick={() =>
                    arrayHelpers.push(generatedInitialValues[item.id][0])
                  }
                >
                  Element hinzufügen
                </button>
              </div>
            ) : null}
          </>
        )}
      />

      <ErrorMessage
        role="region"
        aria-live="polite"
        component="span"
        name={item.id}
        className="error-message"
      />
      {!!item["#description"] && (
        <small className="form-description text-muted form-text">
          {item["#description"]}
        </small>
      )}
    </div>
  );
};

CompositeAddressField.propTypes = {
  item: PropTypes.shape({
    "#states": PropTypes.object,
    id: PropTypes.string,
    "#title": PropTypes.string,
    "#flex": PropTypes.number,
    "#multiple": PropTypes.number,
    "#required": PropTypes.object,
    "#description": PropTypes.string,
  }),
  generatedInitialValues: PropTypes.object,
  compositeIndex: PropTypes.number,
  compositeParent: PropTypes.string,
};

export default CompositeAddressField;
